import { Decoration, DecorationSet, EditorView, RangeSetBuilder, ViewPlugin, ViewUpdate } from '@uiw/react-codemirror'
import { Compartment, Facet } from '@codemirror/state'
import { ASSET_REFERENCE_PATTERN } from './assetReferencePattern'
import { AssetChipWidget } from './AssetChipWidget'

export type AssetLabel = { label: string; typeId: string | undefined }
export type AssetTriggerRange = { from: number; to: number }

export const assetLabelsFacet = Facet.define<Map<string, AssetLabel>, Map<string, AssetLabel>>({
  combine: (inputs) => inputs[inputs.length - 1] ?? new Map(),
})

export const assetChipEditableFacet = Facet.define<boolean, boolean>({
  combine: (inputs) => inputs[inputs.length - 1] ?? false,
})

export const onEditAssetFacet = Facet.define<(range: AssetTriggerRange) => void, (range: AssetTriggerRange) => void>({
  combine: (inputs) => inputs[inputs.length - 1] ?? (() => {}),
})

const assetLabelsCompartment = new Compartment()
const assetChipEditableCompartment = new Compartment()
const onEditAssetCompartment = new Compartment()

export type AssetDecorationConfig = {
  labels: Map<string, AssetLabel>
  editable: boolean
  onEditAsset: (range: AssetTriggerRange) => void
}

export function configureAssetDecorations(view: EditorView, config: AssetDecorationConfig) {
  view.dispatch({
    effects: [
      assetLabelsCompartment.reconfigure(assetLabelsFacet.of(config.labels)),
      assetChipEditableCompartment.reconfigure(assetChipEditableFacet.of(config.editable)),
      onEditAssetCompartment.reconfigure(onEditAssetFacet.of(config.onEditAsset)),
    ],
  })
}

export function initialAssetDecorationExtensions(config: AssetDecorationConfig) {
  return [
    assetLabelsCompartment.of(assetLabelsFacet.of(config.labels)),
    assetChipEditableCompartment.of(assetChipEditableFacet.of(config.editable)),
    onEditAssetCompartment.of(onEditAssetFacet.of(config.onEditAsset)),
  ]
}

function buildDecorations(view: EditorView): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>()
  const labels = view.state.facet(assetLabelsFacet)
  const doc = view.state.doc.toString()

  const matches: { from: number; to: number; uuid: string; versionId?: string }[] = []
  let match
  ASSET_REFERENCE_PATTERN.lastIndex = 0
  while ((match = ASSET_REFERENCE_PATTERN.exec(doc)) !== null) {
    matches.push({
      from: match.index,
      to: match.index + match[0].length,
      uuid: match[1].toLowerCase(),
      versionId: match[2]?.toLowerCase(),
    })
  }

  for (const { from, to, uuid, versionId } of matches) {
    const cursorOverlaps = view.state.selection.ranges.some((range) => range.from < to && range.to > from)
    if (cursorOverlaps) continue

    const info = labels.get(uuid)
    const baseLabel = info?.label ?? `asset://${uuid.slice(0, 8)}…`
    const displayLabel = versionId ? `${baseLabel} @${versionId.slice(0, 8)}` : baseLabel
    builder.add(
      from,
      to,
      Decoration.replace({ widget: new AssetChipWidget(displayLabel, uuid, info?.typeId, from, to, versionId) })
    )
  }

  return builder.finish()
}

const assetDecorationPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet
    constructor(view: EditorView) {
      this.decorations = buildDecorations(view)
    }
    update(update: ViewUpdate) {
      if (
        update.docChanged ||
        update.selectionSet ||
        update.state.facet(assetLabelsFacet) !== update.startState.facet(assetLabelsFacet) ||
        update.state.facet(assetChipEditableFacet) !== update.startState.facet(assetChipEditableFacet)
      ) {
        this.decorations = buildDecorations(update.view)
      }
    }
  },
  { decorations: (v) => v.decorations }
)

export function assetReferenceDecorationPlugin() {
  return assetDecorationPlugin
}
